import {
  Checkbox,
  CheckboxGroup,
  Input,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
} from "@chakra-ui/react";
import type { ContentItem } from "../layout/documentLayout";
import {
  buildCheckAnswer,
  buildRadioAnswer,
  choiceOptions,
  OTHER_LABEL,
  OTHER_VALUE,
  splitCheckAnswer,
  splitRadioAnswer,
  type PatientDocumentAnswer,
} from "./answerFormat";
import { canSubmitWrites, type AnswerWrite } from "./answerWrites";
import { EditorShell } from "./EditorShell";
import { isAnswerChanged, useAnswerWriter, useEditorDraft } from "./editorValues";
import {
  applyCheckExclusivity,
  displayedChildValue,
  exclusiveOptions,
  parentAnswerFamily,
  parentAnswerWrites,
} from "./parentAnswer";
import type { QuestionEditorProps } from "./types";

// Editors for radio, yesNo, dropDown (one answer) and check (many answers). "Other" free text is
// stored as the text itself (radio) or as an extra array item (check), like mobile does.
// Save stays disabled until the reviewer changed the answer (see editorValues.ts), so a stored
// value the editor cannot show (e.g. no longer an option) is never erased by a no-op save.
//
// A child with `parentAnswer` (mobile shows it instead of its parent) also offers the exclusive
// option (first or last per its config) and starts from what mobile shows (the exclusive option
// when the parent holds its value). A check child keeps the exclusive option and the others
// apart. Save writes the child (or deletes / skips it when only the exclusive option is chosen)
// and then the recomputed parent answer, both through `onSaveAnswers` (parentAnswer.ts).

/** The options the editor offers, and how a parentAnswer child's Save is written. */
function useChoiceSetup(props: QuestionEditorProps) {
  const { answers, byId, question } = props;
  const family = parentAnswerFamily(question, byId);
  const writer = useAnswerWriter(props.onSaveAnswers);
  const options = family !== null ? exclusiveOptions(question) : choiceOptions(question);
  // What the editor starts from (and Save compares with)
  const shown =
    family !== null
      ? (displayedChildValue(question, answers, byId) as PatientDocumentAnswer)
      : props.value;

  const save = async (next: PatientDocumentAnswer) => {
    if (family === null) {
      props.onSave(next);
      return;
    }
    const writes = parentAnswerWrites({ answers, byId, child: question, childValue: next }).map(
      (write): AnswerWrite => ({ ...write, answer: write.answer as PatientDocumentAnswer })
    );
    if (await writer.run(writes)) {
      props.onClose();
    }
  };

  /** Save / Retry: also enabled while writes are left by a partial failure */
  const canSubmit = (canSave: boolean) => canSubmitWrites(canSave, writer.pending);

  return { canSubmit, family, options, resetWrites: writer.reset, save, shown };
}

function exclusiveLabel(question: ContentItem): string | null {
  return question.parentAnswer?.exclusiveField?.label ?? null;
}

function OtherTextInput(props: { value: string; onChange: (value: string) => void }) {
  return (
    <Input
      autoFocus={true}
      placeholder="Other (please specify)"
      size="sm"
      value={props.value}
      onChange={(event) => props.onChange(event.target.value)}
    />
  );
}

function SingleChoiceEditor(props: QuestionEditorProps) {
  const { question } = props;
  const { canSubmit, options, resetWrites, save, shown } = useChoiceSetup(props);
  const showOtherOption = question.showOtherOption === true;
  const initial = splitRadioAnswer({ answer: shown, options, showOtherOption });
  const { draft: state, isTouched, updateDraft } = useEditorDraft(() => initial);
  const isOther = state.selected === OTHER_VALUE;
  const next = buildRadioAnswer({ ...state, otherText: state.otherText.trim() });
  const canSave = isTouched && (!isOther || next !== "") && isAnswerChanged(shown, next);
  const storedValue = Array.isArray(shown) ? shown[0] : shown;
  const unlistedValue =
    initial.selected === null && typeof storedValue === "string" && storedValue !== ""
      ? storedValue
      : null;

  const setState: typeof updateDraft = (update) => {
    resetWrites();
    updateDraft(update);
  };

  const handleSelect = (selected: string) => {
    setState((prev) => ({ ...prev, selected: selected === "" ? null : selected }));
  };

  const handleSave = () => void save(next);

  const control =
    question.itemType === "dropDown" ? (
      <Select
        placeholder="Select an option"
        size="sm"
        value={state.selected ?? ""}
        onChange={(event) => handleSelect(event.target.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
        {showOtherOption && <option value={OTHER_VALUE}>{OTHER_LABEL}</option>}
      </Select>
    ) : (
      <RadioGroup value={state.selected ?? ""} onChange={handleSelect}>
        <Stack spacing={2}>
          {options.map((option) => (
            <Radio key={option} size="sm" value={option}>
              {option}
            </Radio>
          ))}
          {showOtherOption && (
            <Radio size="sm" value={OTHER_VALUE}>
              {OTHER_LABEL}
            </Radio>
          )}
        </Stack>
      </RadioGroup>
    );

  return (
    <EditorShell
      canSave={canSubmit(canSave)}
      error={props.error}
      isSaving={props.isSaving}
      label={question.label}
      onCancel={props.onCancel}
      onSave={handleSave}
    >
      <Stack spacing={2}>
        {unlistedValue !== null && !isTouched && (
          <Text color="orange.600" fontSize="xs">
            The stored answer &quot;{unlistedValue}&quot; is not one of the options. It is kept
            unless you choose another answer.
          </Text>
        )}
        {control}
        {isOther && (
          <OtherTextInput
            value={state.otherText}
            onChange={(otherText) => setState((prev) => ({ ...prev, otherText }))}
          />
        )}
      </Stack>
    </EditorShell>
  );
}

function CheckEditor(props: QuestionEditorProps) {
  const { question } = props;
  const { canSubmit, family, options, resetWrites, save, shown } = useChoiceSetup(props);
  const showOtherOption = question.showOtherOption === true;
  const {
    draft: state,
    isTouched,
    updateDraft,
  } = useEditorDraft(() => splitCheckAnswer({ answer: shown, options, showOtherOption }));
  const next = buildCheckAnswer(options, { ...state, otherText: state.otherText.trim() });
  const canSave =
    isTouched &&
    (!state.otherChecked || state.otherText.trim() !== "") &&
    isAnswerChanged(shown, next);
  const groupValue = state.otherChecked ? [...state.selected, OTHER_VALUE] : state.selected;
  const exclusive = family !== null ? exclusiveLabel(question) : null;

  const setState: typeof updateDraft = (update) => {
    resetWrites();
    updateDraft(update);
  };

  const handleChange = (values: (string | number)[]) => {
    const strings = values.map(String);
    setState((prev) => {
      const clicked = {
        ...prev,
        otherChecked: strings.includes(OTHER_VALUE),
        selected: strings.filter((value) => value !== OTHER_VALUE),
      };
      if (exclusive === null) {
        return clicked;
      }
      // Exclusivity works on the displayed values (the "Other" text is one of the others)
      const kept = applyCheckExclusivity(
        question,
        buildCheckAnswer(options, prev),
        buildCheckAnswer(options, clicked)
      );
      return splitCheckAnswer({ answer: kept, options, showOtherOption });
    });
  };

  const handleSave = () => void save(next);

  return (
    <EditorShell
      canSave={canSubmit(canSave)}
      error={props.error}
      isSaving={props.isSaving}
      label={question.label}
      onCancel={props.onCancel}
      onSave={handleSave}
    >
      <Stack spacing={2}>
        <CheckboxGroup value={groupValue} onChange={handleChange}>
          <Stack spacing={2}>
            {options.map((option) => (
              <Checkbox key={option} size="sm" value={option}>
                {option}
              </Checkbox>
            ))}
            {showOtherOption && (
              <Checkbox size="sm" value={OTHER_VALUE}>
                {OTHER_LABEL}
              </Checkbox>
            )}
          </Stack>
        </CheckboxGroup>
        {state.otherChecked && (
          <OtherTextInput
            value={state.otherText}
            onChange={(otherText) => setState((prev) => ({ ...prev, otherText }))}
          />
        )}
      </Stack>
    </EditorShell>
  );
}

export function ChoiceQuestionEditor(props: QuestionEditorProps) {
  return props.question.itemType === "check" ? (
    <CheckEditor {...props} />
  ) : (
    <SingleChoiceEditor {...props} />
  );
}
